import type { ComponentProps, PropsWithChildren, ReactNode } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { ErrorBoundary } from 'react-error-boundary'
import { shouldNotHappen } from 'src/utils/error'

/*
 * ==== Context ================================================================
 */

type SlotContextValue = {
  slots: Record<string, HTMLElement[] | undefined>
  register: (name: string, el: HTMLElement) => void
  unregister: (name: string, el: HTMLElement) => void
  contents: Record<string, string | undefined>
  setContent: (name: string, id: string | undefined) => void
}

const SlotContext = createContext<SlotContextValue | undefined>(undefined)

export function SlotProvider({ children }: PropsWithChildren) {
  const [slots, setSlots] = useState<SlotContextValue['slots']>({})

  const register = useCallback<SlotContextValue['register']>((name, el) => {
    setSlots(slots => {
      return slots[name]?.includes(el)
        ? slots
        : { ...slots, [name]: (slots[name] ?? []).concat(el) }
    })
  }, [])

  const unregister = useCallback<SlotContextValue['unregister']>((name, el) => {
    setSlots(slots => {
      if (slots[name]?.includes(el)) {
        const value = slots[name].filter(it => it !== el)
        return { ...slots, [name]: value.length ? value : undefined }
      } else {
        return slots
      }
    })
  }, [])

  const [contents, setContents] = useState<SlotContextValue['contents']>({})

  const setContent = useCallback<SlotContextValue['setContent']>((name, id) => {
    setContents(contents => {
      return contents[name] !== id ? { ...contents, [name]: id } : contents
    })
  }, [])

  return (
    <SlotContext.Provider
      value={{ slots, register, unregister, contents, setContent }}
    >
      {children}
    </SlotContext.Provider>
  )
}

/*
 * ==== Slot Container (Provider-side) =========================================
 */

export interface SlotProps extends ComponentProps<'div'> {
  name: string
  /**
   * Whether to force mount the slot element even if no content is provided.
   */
  forceMount?: boolean
  /**
   * The fallback content to render when no content is provided.
   */
  children?: ReactNode
}

/**
 * Define a slot container whose content can be provided via `UseSlot`.
 * `Slot` itself renders a plain `div`.
 */
export function Slot({ name, forceMount, children, ...attrs }: SlotProps) {
  const { register, unregister, contents } =
    useContext(SlotContext) ??
    shouldNotHappen('Slot must be used within a SlotProvider')
  const slotId = useId()
  const [el, setEl] = useState<HTMLDivElement | null>(null)

  const hasContent = Boolean(contents[name])
  const shouldMount = forceMount || children || hasContent

  useEffect(() => {
    if (el && shouldMount) {
      register(name, el)
    }

    return () => {
      if (el) unregister(name, el)
    }
  }, [el, name, shouldMount, register, unregister])

  return (
    shouldMount && (
      <div ref={setEl} {...attrs} data-slot-id={slotId}>
        {!hasContent && children}
      </div>
    )
  )
}

/*
 * ==== Slot Content (Client-side) =============================================
 */

export interface UseSlotProps extends PropsWithChildren {
  name: string
}

/**
 * Provide content to a named slot defined by `Slot`.
 */
export function UseSlot({ name, children }: UseSlotProps) {
  const { slots, contents, setContent } =
    useContext(SlotContext) ??
    shouldNotHappen('UseSlot must be used within a SlotProvider')
  const contentId = useId()

  const slotEls = slots[name]
  const slotUsedBy = contents[name]

  useEffect(() => {
    // Each slot only accepts one content (Maybe duplicated to multiple
    // locations if it is a multi-location slot).
    // First mounted wins.
    if (!slotUsedBy || slotUsedBy === contentId) {
      setContent(name, children ? contentId : undefined)
    }

    return () => {
      // Once unmounted, update the content registry only if it is self-owned.
      if (slotUsedBy === contentId) {
        setContent(name, undefined)
      }
    }
  }, [children, contentId, slotUsedBy, name, setContent])

  return (
    slotEls &&
    children &&
    contents[name] === contentId &&
    slotEls.map((s, idx) => (
      <ErrorBoundary key={`${name}:${s.dataset.slotId || idx}`} fallback={null}>
        {createPortal(children, s)}
      </ErrorBoundary>
    ))
  )
}

/*
 * ==== Example ================================================================
 */

/*
<SlotProvider>
  <Slot
    name="banner"
    className="rounded-xl shadow-lg overflow-hidden"
    style={{ width: 300, maxHeight: 80 }}
  >
    <p>Loading</p>
  </Slot>

  <UseSlot name="banner">
    <img src="https://cdn.pixai.art/example/pixai_recap_2025.png" />
  </UseSlot>
</SlotProvider>
*/
